import React, { useState } from 'react';
import { MoreHorizontal, Clock, Check, RotateCcw, Archive, Pencil, UserCheck, ArrowUpRight, X } from 'lucide-react';
import { sourceMeta, isMiloSuggestion, formatDueDate, CATEGORY_LABELS, daysUntil, isOverdue } from './erHelpers';

// Modules we can deep-link into from a task (matches AdminPage tab routing).
const MODULE_LABELS = { vendors: 'Vendors', finance: 'Finance', guests: 'Guests', calendar: 'Calendar' };

/*
 * OperationRow — one task, checkbox-first. Shows its source (Manual / AI
 * Suggested / AI Detected), a one-line note, meta chips, a deep-link into the
 * relevant module, and an overflow menu for the full manual lifecycle.
 *
 * One checkbox per row — the mark-done toggle. Per-item delete/dismiss lives in
 * the overflow ("3-dot") menu so every card can be removed individually.
 */
export default function OperationRow({
  op, busy, todayIso, haloed = false,
  onToggleComplete, onAccept, onDismiss, onEdit, onSnooze,
  onMarkWaiting, onReopen, onArchive, onNavigateToModule, onAssist,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);

  const src = sourceMeta(op);
  const suggestion = isMiloSuggestion(op);
  const completed = op.state === 'completed';
  const stateOwned = ['waiting', 'delegated', 'snoozed'].includes(op.state);
  const derivedDone = op.derived_status === 'done' && !op.derived_confirmed && !completed && !stateOwned;
  const derivedBlocked = op.derived_status === 'blocked' && !completed;
  const overdue = isOverdue(op, todayIso);

  const dueText = formatDueDate(op.due_date);
  const dueDays = daysUntil(op.due_date);
  const dueSoon = dueDays != null && dueDays >= 0 && dueDays <= 7;
  const categoryText = CATEGORY_LABELS[op.category] || null;
  const moduleLabel = op.related_module && MODULE_LABELS[op.related_module];
  const canNavigate = moduleLabel && typeof onNavigateToModule === 'function';
  const canAssist = op.category === 'communications' && typeof onAssist === 'function' && !completed;

  const reasoning = op.derived_evidence || op.milo_reasoning || null;
  const blocked = op.state === 'blocked' || derivedBlocked;
  const noteClass = derivedDone ? 'evidence' : blocked ? 'block' : '';
  const noteText = derivedDone ? op.derived_evidence
    : blocked ? (op.derived_evidence || op.milo_reasoning || op.detail)
    : op.detail;

  const taskClass =
    `ero-glass ero-task${completed ? ' checked' : ''}` +
    `${suggestion ? ' suggested' : ''}${derivedDone ? ' detected' : ''}` +
    `${haloed ? ' ero-halo' : ''}` +
    `${menuOpen ? ' ero-menu-open' : ''}` +
    `${suggestion || completed ? '' : ' ero-lift'}`;

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className={taskClass} data-op-id={op._id} data-testid={`op-${op._id}`}>
      <button
        type="button"
        className={`ero-check${completed ? ' on' : ''}${derivedDone ? ' soft' : ''}`}
        disabled={busy}
        aria-label={completed ? 'Reopen task' : 'Mark task done'}
        onClick={() => onToggleComplete?.(op)}
      >
        {(completed || derivedDone) ? <Check size={13} aria-hidden="true" /> : null}
      </button>

      <div className="ero-tbody">
        <div className="ero-ttop">
          <span className="ero-ttitle">{op.title}</span>
          <span className={`ero-src ${src.key}`}>{src.label}</span>
        </div>

        <div className="ero-tmeta">
          {overdue && <span className="overdue">Overdue</span>}
          {overdue && dueText && <span className="sep">·</span>}
          {dueText && <span className={dueSoon ? 'due-soon' : ''}>{dueText}</span>}
          {op.waiting_on && <span className="sep">·</span>}
          {op.waiting_on && <span>Waiting on {op.waiting_on}</span>}
          {op.delegated_to && <span className="sep">·</span>}
          {op.delegated_to && <span>Delegated to {op.delegated_to}</span>}
          {categoryText && <span className="sep">·</span>}
          {categoryText && <span>{categoryText}</span>}
        </div>

        {noteText && (
          <div className={`ero-tnote ${noteClass}`}>
            {(derivedDone || suggestion) && <span className="ni" aria-hidden="true">✦</span>}
            <span>{noteText}</span>
          </div>
        )}

        {whyOpen && reasoning && !noteText && (
          <div className="ero-tnote"><span className="ni" aria-hidden="true">✦</span><span>{reasoning}</span></div>
        )}

        {op.budget_tip && !completed && (
          <div className="ero-tnote" style={{ color: 'var(--ero-ink-3)' }}>
            <span className="ni" aria-hidden="true" style={{ color: 'var(--ero-gold-deep)' }}>$</span>
            <span>{op.budget_tip}</span>
          </div>
        )}
      </div>

      <div className="ero-tactions">
        {derivedDone ? (
          <>
            <button type="button" className="ero-btn primary" disabled={busy} onClick={() => onToggleComplete?.(op)}>
              Confirm done
            </button>
            <button type="button" className="ero-twhy" onClick={() => onSnooze?.(op)}>Not yet</button>
          </>
        ) : suggestion ? (
          <>
            <button type="button" className="ero-btn primary" disabled={busy} onClick={() => onAccept?.(op)}>
              <Check size={13} aria-hidden="true" /> Accept
            </button>
            <button type="button" className="ero-twhy" onClick={() => onDismiss?.(op)}>Dismiss</button>
          </>
        ) : canNavigate ? (
          <button type="button" className="ero-btn" disabled={busy}
                  onClick={() => onNavigateToModule(op.related_module, op.related_entity_id)}>
            Open in {moduleLabel} <ArrowUpRight size={13} aria-hidden="true" />
          </button>
        ) : canAssist ? (
          <button type="button" className="ero-btn primary" disabled={busy}
                  onClick={() => onAssist(op)}>
            Help me send this <ArrowUpRight size={13} aria-hidden="true" />
          </button>
        ) : reasoning ? (
          <button type="button" className="ero-twhy" onClick={() => setWhyOpen((v) => !v)}>Why?</button>
        ) : null}

        <div style={{ position: 'relative' }}>
          <button type="button" className="ero-imore" aria-label="More actions" disabled={busy}
                  onClick={() => setMenuOpen((v) => !v)}>
            <MoreHorizontal size={16} aria-hidden="true" />
          </button>
          {menuOpen && (
            <div className="ero-menu" role="menu" onMouseLeave={closeMenu}>
              {!completed && !suggestion && (
                <button type="button" onClick={() => { closeMenu(); onEdit?.(op); }}>
                  <Pencil size={14} aria-hidden="true" /> Edit
                </button>
              )}
              {!completed && !suggestion && op.state !== 'snoozed' && (
                <button type="button" onClick={() => { closeMenu(); onSnooze?.(op); }}>
                  <Clock size={14} aria-hidden="true" /> Snooze a week
                </button>
              )}
              {!completed && !suggestion && op.state !== 'waiting' && op.state !== 'delegated' && (
                <button type="button" onClick={() => { closeMenu(); onMarkWaiting?.(op); }}>
                  <UserCheck size={14} aria-hidden="true" /> Mark waiting
                </button>
              )}
              {canNavigate && (
                <button type="button" onClick={() => { closeMenu(); onNavigateToModule(op.related_module, op.related_entity_id); }}>
                  <ArrowUpRight size={14} aria-hidden="true" /> Open in {moduleLabel}
                </button>
              )}
              {completed && (
                <button type="button" onClick={() => { closeMenu(); onReopen?.(op); }}>
                  <RotateCcw size={14} aria-hidden="true" /> Reopen
                </button>
              )}
              <div className="ero-menu-sep" />
              {suggestion ? (
                <button type="button" className="danger" onClick={() => { closeMenu(); onDismiss?.(op); }}>
                  <X size={14} aria-hidden="true" /> Dismiss
                </button>
              ) : (
                <button type="button" className="danger" onClick={() => { closeMenu(); onArchive?.(op); }}>
                  <Archive size={14} aria-hidden="true" /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
